import { BookOpen, CircleHelp, FileText, MessageSquare, Sparkles } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/toast";

export interface HelpMenuProps {
  projectName: string | null;
  pageContext: string;
  onOpenZot: () => void;
  onOpenFeedback: () => void;
}

/**
 * Top bar help menu with accessible documentation, guidelines, feedback, and Ask Zot actions.
 * Reuses existing real destinations and never invents placeholder URLs.
 */
export function HelpMenu({ onOpenZot, onOpenFeedback }: HelpMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleDocuments = () => {
    setOpen(false);
    navigate("/help");
  };

  const handleGuideline = () => {
    toast("Guidelines are not available yet.");
  };

  const handleAskZot = (event: React.MouseEvent) => {
    if (location.pathname === "/zot") {
      event.preventDefault();
      setOpen(false);
      const composer =
        document.getElementById("zot-message") ?? document.getElementById("zot-home-message");
      composer?.focus();
      return;
    }
    setOpen(false);
    onOpenZot();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <div className="relative">
        <DropdownMenuTrigger
          aria-label="Help"
          className="size-11 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors md:size-8"
        >
          <CircleHelp className="size-4" aria-hidden />
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          side="bottom"
          className="w-64 p-1.5"
          aria-label="Help menu"
        >
          <DropdownMenuItem
            className="flex items-start gap-2.5 p-2 cursor-pointer"
            onClick={handleDocuments}
          >
            <BookOpen className="size-4 shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
            <div className="flex flex-col text-left">
              <span className="font-medium text-xs text-foreground">Documents</span>
              <span className="text-xs text-muted-foreground">Guides and reference</span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="flex items-start gap-2.5 p-2 cursor-pointer"
            onClick={handleGuideline}
          >
            <FileText className="size-4 shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
            <div className="flex flex-col text-left">
              <span className="font-medium text-xs text-foreground">Guideline</span>
              <span className="text-xs text-muted-foreground">How we recommend using Zworks</span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-1" />

          <DropdownMenuItem
            className="flex items-start gap-2.5 p-2 cursor-pointer"
            onClick={onOpenFeedback}
          >
            <MessageSquare className="size-4 shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
            <div className="flex flex-col text-left">
              <span className="font-medium text-xs text-foreground">Submit feedback</span>
              <span className="text-xs text-muted-foreground">Tell us what to fix or build</span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="flex items-start gap-2.5 p-2 cursor-pointer"
            onClick={handleAskZot}
          >
            <Sparkles className="size-4 shrink-0 mt-0.5 text-accent-text" aria-hidden />
            <div className="flex flex-col text-left">
              <span className="font-medium text-xs text-foreground">Ask Zot</span>
              <span className="text-xs text-muted-foreground">
                Get an answer from your project record
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
}
